/**
 * CompliancePage — Batch #14.326 : calendrier de conformité (cabinet → tous les dossiers · ou un dossier précis).
 * Échéances générées des réglages (fin d'exercice, TPS/TVQ, DAS, paie, acomptes) · en retard / 7 jours / à venir · coche « fait ».
 * ?master=<id> ouvre directement un dossier.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BellRing, CalendarClock, Settings2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, errMsg, inputCls } from "./comptabilite/_shared";
import { ComplianceTable, StatusCards } from "./compliance/ComplianceTable";
import { ComplianceSettings } from "./compliance/ComplianceSettings";

const iso = (d) => d.toISOString().slice(0, 10);

export default function CompliancePage() {
  const [sp, setSp] = useSearchParams();
  const masterId = sp.get("master") || "";
  const [masters, setMasters] = useState([]);
  const [days, setDays] = useState(60);
  const [range, setRange] = useState(() => { const t = new Date(); const a = new Date(t); a.setDate(a.getDate() - 90); const b = new Date(t); b.setFullYear(b.getFullYear() + 1); return { from: iso(a), to: iso(b) }; });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [filter, setFilter] = useState({ status: "", category: "" });

  useEffect(() => { api.get("/masters").then((r) => setMasters(r.data?.items || [])).catch(() => setMasters([])); }, []);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = masterId ? await api.get(`/admin/compliance/${masterId}`, { params: { from: range.from, to: range.to } }) : await api.get("/admin/compliance/overview", { params: { days } });
      setData(r.data);
    } catch (e) { toast.error(errMsg(e, "Chargement impossible")); } finally { setBusy(false); }
  }, [masterId, range, days]);
  useEffect(() => { load(); }, [load]);

  const mark = async (row, done) => {
    try { await api.post(`/admin/compliance/${row.master_id || masterId}/mark`, { key: row.key, done }); toast.success(done ? "Échéance marquée comme faite" : "Échéance rouverte"); load(); }
    catch (e) { toast.error(errMsg(e, "Erreur")); }
  };
  const runReminders = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/compliance/run-reminders"); toast.success(`${r.data.sent} rappel(s) envoyé(s)${r.data.emailed ? " + courriel" : ""}`); }
    catch (e) { toast.error(errMsg(e, "Erreur")); } finally { setBusy(false); }
  };
  const rows = useMemo(() => (data?.rows || []).filter((r) => (!filter.status || r.status === filter.status) && (!filter.category || r.category === filter.category)), [data, filter]);
  const categories = useMemo(() => [...new Set((data?.rows || []).map((r) => r.category))], [data]);

  return (
    <div className="space-y-4 p-4 sm:p-6 max-w-7xl mx-auto" data-testid="compliance-page">
      <div className="bg-gradient-to-r from-amber-50 via-amber-50 to-white border-2 border-amber-200 rounded-2xl p-5 sm:p-6 shadow-sm flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-amber-600 text-white flex items-center justify-center shadow"><CalendarClock className="w-6 h-6" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-slate-900">Calendrier de conformité</h1>
          <p className="text-xs sm:text-sm text-slate-600 mt-0.5">T2 / CO-17 · T1 · TPS/TVQ · DAS · T4/RL-1 · CNESST · CCQ · REQ · acomptes · clôture — générés automatiquement des réglages de chaque dossier. Rappels J-7 · J-2 · jour J (cloche, push, courriel).</p>
        </div>
        <Btn tone="slate" icon={BellRing} onClick={runReminders} busy={busy} testId="compliance-run-reminders">Rappels maintenant</Btn>
      </div>

      <div className="flex flex-wrap items-end gap-2 bg-white border rounded-xl p-3">
        <label className="text-xs text-slate-600 flex-1 min-w-[220px]">Dossier
          <select className={inputCls} value={masterId} onChange={(e) => { const v = e.target.value; setSp(v ? { master: v } : {}); setData(null); }} data-testid="compliance-master-select">
            <option value="">Tous les dossiers (vue cabinet)</option>
            {masters.map((m) => <option key={m.id} value={m.id}>{m.client_number ? `${m.client_number} · ` : ""}{m.couple_display_name}</option>)}
          </select>
        </label>
        {masterId ? (
          <>
            <label className="text-xs text-slate-600">Du<input type="date" className={inputCls} value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} data-testid="compliance-from" /></label>
            <label className="text-xs text-slate-600">Au<input type="date" className={inputCls} value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} data-testid="compliance-to" /></label>
            <Btn tone="slate" icon={Settings2} onClick={() => setShowSettings(true)} testId="compliance-settings-btn">Réglages du dossier</Btn>
          </>
        ) : (
          <label className="text-xs text-slate-600">Horizon
            <select className={inputCls} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="compliance-horizon">
              {[30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>{d} jours</option>)}
            </select>
          </label>
        )}
        <label className="text-xs text-slate-600">Statut
          <select className={inputCls} value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))} data-testid="compliance-filter-status">
            <option value="">Tous</option><option value="overdue">En retard</option><option value="soon">7 jours</option><option value="upcoming">À venir</option><option value="done">Faits</option>
          </select>
        </label>
        <label className="text-xs text-slate-600">Catégorie
          <select className={inputCls} value={filter.category} onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))} data-testid="compliance-filter-category">
            <option value="">Toutes</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>

      {data && <StatusCards counts={data.counts} onPick={(s) => setFilter((f) => ({ ...f, status: f.status === s ? "" : s }))} active={filter.status} />}
      {data && masterId && (
        <div className="text-xs text-slate-600 flex flex-wrap gap-3 bg-amber-50/60 border border-amber-100 rounded-lg px-3 py-2" data-testid="compliance-master-meta">
          <span>Entité : <b>{{ corp: "Société (T2 / CO-17)", sole: "Travailleur autonome (T1 15 juin)", individual: "Particulier (T1 30 avril)" }[data.entity_type]}</b></span>
          <span>Fin d'exercice : <b>{data.fiscal_year_end}</b></span>
          <span>Expertises : <b>{(data.expertises || []).join(", ") || "—"}</b></span>
        </div>
      )}
      <ComplianceTable rows={rows} showMaster={!masterId} onMark={mark} loading={busy && !data} onOpenMaster={(id) => setSp({ master: id })} />
      {showSettings && masterId && <ComplianceSettings masterId={masterId} settings={data?.settings} entityType={data?.entity_type} onClose={() => setShowSettings(false)} onSaved={() => { setShowSettings(false); load(); }} />}
    </div>
  );
}
